<script setup lang="ts">
import type { CommentCreateInput, CommentSubmittedPayload } from '~/types/blog'
import { STORAGE_KEYS } from '~/utils/storage'

const blogApi = useBlogApi()
const props = withDefaults(
  defineProps<{
    contentId: number
    parentId?: number
    variant?: 'default' | 'compact'
    showEmojiPicker?: boolean
  }>(),
  { variant: 'default', showEmojiPicker: true }
)
const emit = defineEmits<{ (e: 'submitted', payload: CommentSubmittedPayload): void }>()
const COMMENT_PROFILE_KEY = STORAGE_KEYS.COMMENT_PROFILE
const { isAuthenticated, userName: authUserName } = usePluginAuth()

const submitting = ref(false)
const showEmoji = ref(false)
const contentTextareaRef = ref<HTMLTextAreaElement | null>(null)
const colorMode = useColorMode()
const emojiWrapRef = ref<HTMLElement | null>(null)
const emojiToggleRef = ref<HTMLElement | null>(null)
const emojiPanelRef = ref<HTMLElement | null>(null)
const emojiPanelStyle = ref<Record<string, string>>({})
const emojiPanelPlacement = ref<'up' | 'down'>('down')
const captcha = ref<{ captchaId?: string; picPath?: string; captchaLength?: number; openCaptcha?: boolean } | null>(null)
const commentSubmitSetting = reactive({
  commentCaptchaEnabled: false,
  ipCommentIntervalSeconds: 0
})
const form = reactive({
  author: '',
  email: '',
  url: '',
  content: '',
  emailNotify: true,
  captcha: ''
})
const errorMessage = ref('')
const isCompact = computed(() => props.variant === 'compact')
const canShowEmojiPicker = computed(() => props.showEmojiPicker !== false)

type CommentProfileStorage = {
  author?: string
  email?: string
  url?: string
}

type EmojiMartPickerElement = HTMLElement & {
  update?: (props: Record<string, unknown>) => void
}

type EmojiMartSelectPayload = {
  native?: string
}

let emojiPicker: EmojiMartPickerElement | null = null

const emojiPickerTheme = () => (colorMode.value === 'dark' ? 'dark' : 'light')

const getEmojiPanelWidth = () => {
  const panelWidth = emojiPanelRef.value?.getBoundingClientRect().width || 0
  const pickerWidth = emojiPicker?.getBoundingClientRect().width || 0
  return Math.max(panelWidth, pickerWidth)
}

const resolveEmojiPanelLeft = (preferredLeft: number, viewportGap: number) => {
  const minLeft = viewportGap
  const panelWidth = getEmojiPanelWidth()
  if (!panelWidth) {
    return Math.max(minLeft, preferredLeft)
  }
  const maxLeft = window.innerWidth - panelWidth - viewportGap
  if (maxLeft < minLeft) {
    return minLeft
  }
  return Math.min(Math.max(minLeft, preferredLeft), maxLeft)
}

const updateEmojiPanelPosition = (recalculatePlacement = false) => {
  if (!import.meta.client || !emojiToggleRef.value) {
    return
  }
  const rect = emojiToggleRef.value.getBoundingClientRect()
  const header = document.querySelector('.header-wrap') as HTMLElement | null
  const headerBottom = Math.max(0, Math.round(header?.getBoundingClientRect().bottom || 0))
  const viewportGap = 12
  const triggerGap = 8
  const headerGap = 6
  const preferredPanelHeight = 360
  const left = resolveEmojiPanelLeft(rect.left, viewportGap)
  const minTop = headerBottom + headerGap
  const triggerBelowTop = Math.max(minTop, rect.bottom + triggerGap)
  const triggerBelowSpace = window.innerHeight - triggerBelowTop - viewportGap
  const headerBelowSpace = window.innerHeight - minTop - viewportGap
  const upTop = rect.top - triggerGap - preferredPanelHeight
  const canOpenUp = upTop >= minTop
  const canOpenDown = triggerBelowSpace >= preferredPanelHeight

  if (recalculatePlacement) {
    emojiPanelPlacement.value = !canOpenDown && canOpenUp ? 'up' : 'down'
  } else if (emojiPanelPlacement.value === 'up' && !canOpenUp && headerBelowSpace >= preferredPanelHeight) {
    emojiPanelPlacement.value = 'down'
  } else if (emojiPanelPlacement.value === 'down' && !canOpenDown && canOpenUp) {
    emojiPanelPlacement.value = 'up'
  }

  const top =
    emojiPanelPlacement.value === 'up'
      ? Math.max(minTop, upTop)
      : canOpenDown
        ? triggerBelowTop
        : minTop

  emojiPanelStyle.value = {
    left: `${left}px`,
    top: `${top}px`,
    height: `${preferredPanelHeight}px`
  }
}

const updateEmojiPanelPositionOnViewportChange = () => updateEmojiPanelPosition()

const clearEmojiPicker = () => {
  emojiPicker?.remove()
  emojiPicker = null
}

const toggleEmojiPanel = () => {
  if (!showEmoji.value) {
    updateEmojiPanelPosition(true)
  }
  showEmoji.value = !showEmoji.value
}

const mountEmojiPicker = async () => {
  if (!canShowEmojiPicker.value || !import.meta.client || !showEmoji.value || !emojiPanelRef.value || emojiPicker) {
    return
  }
  const [{ Picker }, dataModule, i18nModule] = await Promise.all([
    import('emoji-mart'),
    import('@emoji-mart/data'),
    import('@emoji-mart/data/i18n/zh.json')
  ])
  if (!canShowEmojiPicker.value || !showEmoji.value || !emojiPanelRef.value || emojiPicker) {
    return
  }
  const picker = new Picker({
    data: dataModule.default,
    i18n: i18nModule.default,
    locale: 'zh',
    theme: emojiPickerTheme(),
    set: 'native',
    previewPosition: 'none',
    skinTonePosition: 'search',
    emojiButtonRadius: '10px',
    onEmojiSelect: (emoji: EmojiMartSelectPayload) => {
      if (emoji.native) {
        insertEmoji(emoji.native)
      }
    }
  }) as unknown as EmojiMartPickerElement
  emojiPanelRef.value.appendChild(picker)
  emojiPicker = picker
  requestAnimationFrame(() => {
    if (showEmoji.value) {
      updateEmojiPanelPosition()
    }
  })
}

const loadCommentProfile = () => {
  if (!import.meta.client) {
    return
  }
  try {
    const raw = localStorage.getItem(COMMENT_PROFILE_KEY)
    if (!raw) {
      return
    }
    const parsed = JSON.parse(raw) as CommentProfileStorage
    if (!form.author) {
      form.author = String(parsed?.author || '').trim()
    }
    if (!form.email) {
      form.email = String(parsed?.email || '').trim()
    }
    if (!form.url) {
      form.url = String(parsed?.url || '').trim()
    }
  } catch {
    localStorage.removeItem(COMMENT_PROFILE_KEY)
  }
}

const saveCommentProfile = (profile: CommentProfileStorage) => {
  if (!import.meta.client) {
    return
  }
  try {
    localStorage.setItem(
      COMMENT_PROFILE_KEY,
      JSON.stringify({
        author: String(profile.author || '').trim(),
        email: String(profile.email || '').trim(),
        url: String(profile.url || '').trim()
      })
    )
  } catch {
    // Ignore localStorage failures (private mode/quota).
  }
}

const insertEmoji = (emoji: string) => {
  const textarea = contentTextareaRef.value
  if (!textarea) {
    form.content += emoji
    showEmoji.value = false
    return
  }
  const start = textarea.selectionStart || 0
  const end = textarea.selectionEnd || 0
  const value = form.content || ''
  form.content = `${value.slice(0, start)}${emoji}${value.slice(end)}`
  nextTick(() => {
    const nextPos = start + emoji.length
    textarea.focus()
    textarea.setSelectionRange(nextPos, nextPos)
  })
  showEmoji.value = false
}

const onDocumentClick = (event: MouseEvent) => {
  if (!showEmoji.value) {
    return
  }
  const target = event.target as Node | null
  if (
    target &&
    emojiWrapRef.value &&
    !emojiWrapRef.value.contains(target) &&
    !emojiPanelRef.value?.contains(target)
  ) {
    showEmoji.value = false
  }
}

onMounted(() => {
  loadCommentProfile()
  void loadCommentSubmitSetting()
  document.addEventListener('click', onDocumentClick)
  window.addEventListener('scroll', updateEmojiPanelPositionOnViewportChange, true)
  window.addEventListener('resize', updateEmojiPanelPositionOnViewportChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  window.removeEventListener('scroll', updateEmojiPanelPositionOnViewportChange, true)
  window.removeEventListener('resize', updateEmojiPanelPositionOnViewportChange)
  clearEmojiPicker()
})

watch(showEmoji, async (visible) => {
  if (!visible) {
    clearEmojiPicker()
    emojiPanelPlacement.value = 'down'
    return
  }
  await nextTick()
  updateEmojiPanelPosition()
  await mountEmojiPicker()
})

watch(() => colorMode.value, () => {
  emojiPicker?.update?.({ theme: emojiPickerTheme() })
})

const submit = async () => {
  errorMessage.value = ''
  if (!form.content.trim()) {
    errorMessage.value = '评论内容不能为空'
    return
  }
  if (!isAuthenticated.value && (!form.author.trim() || !form.email.trim())) {
    errorMessage.value = '昵称、邮箱和评论内容不能为空'
    return
  }
  if (commentSubmitSetting.commentCaptchaEnabled && !form.captcha.trim()) {
    errorMessage.value = '请输入验证码'
    return
  }
  const payload: CommentCreateInput = {
    contentId: props.contentId,
    parentId: props.parentId || 0,
    author: form.author.trim(),
    email: form.email.trim(),
    url: form.url.trim(),
    content: form.content.trim(),
    emailNotify: form.emailNotify,
    captcha: commentSubmitSetting.commentCaptchaEnabled ? form.captcha.trim() : '',
    captchaId: commentSubmitSetting.commentCaptchaEnabled ? String(captcha.value?.captchaId || '') : ''
  }
  try {
    submitting.value = true
    let submittedPayload: CommentSubmittedPayload
    if (isAuthenticated.value) {
      const result = await blogApi.createLoginComment({
        contentId: payload.contentId,
        parentId: payload.parentId,
        content: payload.content,
        emailNotify: payload.emailNotify,
        captcha: payload.captcha,
        captchaId: payload.captchaId
      })
      submittedPayload = {
        ID: result.ID,
        contentId: result.contentId,
        parentId: result.parentId,
        author: result.author,
        emailHash: result.emailHash,
        url: result.url,
        content: result.content,
        status: result.status,
        userId: result.userId,
        authenticated: result.authenticated,
        emailNotify: result.emailNotify,
        createdAt: result.CreatedAt
      }
    } else {
      await blogApi.createComment(payload)
      saveCommentProfile({
        author: payload.author,
        email: payload.email,
        url: payload.url
      })
      submittedPayload = {
        ...payload,
        createdAt: new Date().toISOString()
      }
    }
    form.content = ''
    form.captcha = ''
    emit('submitted', submittedPayload)
    if (commentSubmitSetting.commentCaptchaEnabled) {
      await refreshCaptcha()
    }
  } catch (error) {
    errorMessage.value = (error as Error)?.message || '提交失败'
    if (commentSubmitSetting.commentCaptchaEnabled) {
      await refreshCaptcha()
    }
  } finally {
    submitting.value = false
  }
}

const refreshCaptcha = async () => {
  try {
    captcha.value = await blogApi.getCaptcha()
  } catch (error) {
    captcha.value = null
  }
}

const loadCommentSubmitSetting = async () => {
  try {
    const setting = await blogApi.getCommentSubmitSetting()
    commentSubmitSetting.commentCaptchaEnabled = setting?.commentCaptchaEnabled === true
    commentSubmitSetting.ipCommentIntervalSeconds = Number(setting?.ipCommentIntervalSeconds || 0)
  } catch (error) {
    commentSubmitSetting.commentCaptchaEnabled = false
    commentSubmitSetting.ipCommentIntervalSeconds = 0
  }
  if (commentSubmitSetting.commentCaptchaEnabled) {
    await refreshCaptcha()
    return
  }
  captcha.value = null
}
</script>

<template>
  <form class="card comment-form" :class="{ compact: isCompact, 'without-emoji': !canShowEmojiPicker }" @submit.prevent="submit">
    <div v-if="isAuthenticated" class="login-comment-profile">
      <i class="fa-regular fa-circle-user" aria-hidden="true" />
      <span>已登录至 {{ authUserName }}，将使用本账号进行评论</span>
    </div>

    <div v-else class="grid-row">
      <label>
        <span class="required">昵称</span>
        <input v-model="form.author" type="text" maxlength="120" />
      </label>
      <label>
        <span class="required">邮箱</span>
        <input v-model="form.email" type="email" maxlength="160" />
      </label>
    </div>

    <label v-if="!isAuthenticated && !isCompact">
      <span>网址（可选）</span>
      <input v-model="form.url" type="url" maxlength="500" placeholder="https://" />
    </label>

    <label>
      <span class="required">评论内容</span>
      <div class="comment-editor">
        <textarea ref="contentTextareaRef" v-model="form.content" :rows="isCompact ? 2 : 5" maxlength="5000" />
        <div v-if="canShowEmojiPicker" ref="emojiWrapRef" class="emoji-wrap">
          <button ref="emojiToggleRef" class="emoji-toggle" :class="{ open: showEmoji }" type="button" @click.stop="toggleEmojiPanel">
            OωO
          </button>
        </div>
      </div>
    </label>

    <label v-if="commentSubmitSetting.commentCaptchaEnabled">
      <span class="required">验证码</span>
      <div class="captcha-row">
        <input v-model="form.captcha" type="text" :maxlength="captcha?.captchaLength || 6" />
        <button class="captcha-refresh-btn" type="button" @click="refreshCaptcha">
          <img v-if="captcha?.picPath" class="captcha-img" :src="captcha.picPath" alt="captcha" />
          <span v-else>刷新</span>
        </button>
      </div>
    </label>

    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>

    <div class="submit-row">
      <button class="submit-btn" type="submit" :disabled="submitting">
        <i class="fa-regular fa-paper-plane" aria-hidden="true" />{{ submitting ? '提交中' : '提交评论' }}
      </button>
      <label class="notify-row">
        <input v-model="form.emailNotify" type="checkbox" />
        <span>当有回复时邮件通知我</span>
      </label>
    </div>
  </form>
  <ClientOnly>
    <Teleport to="body">
      <div v-if="canShowEmojiPicker && showEmoji" ref="emojiPanelRef" class="emoji-panel" :style="emojiPanelStyle" />
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.comment-form {
  --comment-input-height: 42px;
  --comment-inline-gap: 10px;
  display: grid;
  gap: 12px;
  padding: 16px;
}

.comment-form.compact {
  --comment-input-height: 36px;
  --comment-inline-gap: 8px;
  gap: 8px;
  border: 0;
  padding: 10px;
  background: color-mix(in srgb, var(--bg) 70%, var(--bg-soft) 30%);
  box-shadow: none;
}

.grid-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--comment-inline-gap);
}

.login-comment-profile {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: var(--comment-input-height);
  padding: 8px 10px;
  border: 1px solid color-mix(in srgb, var(--brand) 20%, var(--line) 80%);
  border-radius: 10px;
  color: var(--text-soft);
  background: color-mix(in srgb, var(--brand-soft) 46%, var(--bg-soft) 54%);
}

.login-comment-profile i {
  color: var(--brand);
}

.login-comment-profile span {
  font-size: 13px;
}

.comment-form.compact .login-comment-profile {
  border-radius: 8px;
}

label {
  display: grid;
  gap: 6px;
}

.comment-form.compact label {
  gap: 4px;
}

label span {
  font-size: 13px;
  color: var(--text-soft);
}

label span.required::after {
  content: '*';
  margin-left: 4px;
  color: #d64545;
}

.comment-editor {
  --emoji-offset: 10px;
  position: relative;
}

.comment-form.compact .comment-editor {
  --emoji-offset: 8px;
}

.emoji-wrap {
  position: absolute;
  left: var(--emoji-offset);
  bottom: var(--emoji-offset);
}

.emoji-toggle {
  display: inline-flex;
  align-items: center;
  border: 0;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 12px;
  color: color-mix(in srgb, var(--text-soft) 88%, var(--brand) 12%);
  background: color-mix(in srgb, var(--bg) 74%, var(--bg-soft) 26%);
  cursor: pointer;
  transition:
    background 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease;
}

.emoji-toggle.open {
  color: var(--brand);
  background: color-mix(in srgb, var(--brand) 14%, var(--bg-soft) 86%);
}

.emoji-toggle:hover,
.emoji-toggle:focus-visible {
  color: var(--brand);
  background: color-mix(in srgb, var(--brand) 10%, var(--bg) 90%);
  box-shadow: 0 0 10px color-mix(in srgb, var(--brand) 10%, transparent);
}

.emoji-toggle:focus-visible {
  outline: none;
}

.emoji-panel {
  position: fixed;
  z-index: 2200;
  box-sizing: border-box;
  width: min(354px, calc(100vw - 24px));
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--bg);
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.16);
  overflow: hidden;
}

.emoji-panel :deep(em-emoji-picker) {
  --rgb-accent: 75, 124, 191;
  --shadow: none;
  --border-radius: 12px;
  --font-family: inherit;
  width: 100%;
  height: 100%;
  min-height: 0;
}

input,
textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 10px;
  color: var(--text);
  background: var(--bg-soft);
}

input {
  height: var(--comment-input-height);
}

.comment-form.compact input,
.comment-form.compact textarea {
  border-radius: 8px;
  padding: 8px;
}

textarea {
  display: block;
  min-height: 100px;
  padding-bottom: 44px;
  resize: vertical;
}

.comment-form.compact textarea {
  min-height: 62px;
  padding-bottom: 36px;
}

.comment-form.without-emoji textarea {
  padding-bottom: 8px;
}

.notify-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-soft);
}

.comment-form.compact .notify-row {
  background: transparent;
  gap: 6px;
  font-size: 12px;
}

.submit-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.comment-form.compact .submit-row {
  gap: 8px;
}

.notify-row input {
  width: 14px;
  height: 14px;
  padding: 0;
}

.captcha-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--comment-inline-gap);
  align-items: center;
}

.captcha-refresh-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--line);
  background: var(--bg-soft);
  border-radius: 10px;
  padding: 0;
  min-width: 96px;
  height: var(--comment-input-height);
  cursor: pointer;
  overflow: hidden;
}

.captcha-img {
  width: auto;
  height: 100%;
  display: block;
  object-fit: contain;
}

.error {
  color: #d64545;
  font-size: 13px;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  border: 0;
  border-radius: 10px;
  padding: 9px 14px;
  color: #fff;
  cursor: pointer;
  background: var(--brand);
}

.comment-form.compact .submit-btn {
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 13px;
}

.submit-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

@media (max-width: 760px) {
  .grid-row {
    grid-template-columns: 1fr;
  }
}
</style>
